import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useLayout } from '../contexts/LayoutContext';
import apiService from '../services/apiService';
import { useConversationRefresh } from '../hooks/useConversationRefresh';
import { SkeletonMessage } from './common/SkeletonLoader';
import AssessmentView from './assessment/AssessmentView';
import RoleplayView from './roleplay/RoleplayView';
import ChatView from './chat/ChatView';
import CohortSummaryPage from './reporting/cohort/CohortSummaryPage';
import CohortThemesPage from './reporting/cohort/CohortThemesPage';
import cohortSetLabel from './reporting/cohort/cohortSetLabel';
import '../styles/chat.css';

const ConversationRouter = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setActiveConversationId } = useLayout();
  const { triggerRefresh } = useConversationRefresh();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!conversationId || !user) return;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setActiveConversationId(conversationId);

        const data = await apiService.get(`/api/v1/conversations/${conversationId}/context`, user);
        let conversationData = null;

        if (data?.success && data?.data) {
          conversationData = data.data.conversation || (data.data.id ? data.data : null);
        }

        if (!conversationData?.id) throw new Error('Invalid conversation data received');

        setConversation(conversationData);

        // messages is now { total, returned, turns: [] } — extract turns
        const msgList = conversationData.messages?.turns || conversationData.messages || [];
        const transformedMessages = msgList.map(msg => ({
          id: msg.id,
          content: msg.content || '',
          role: msg.sender,
          timestamp: msg.timestamp,
          sender: msg.sender,
          messageOrder: msg.messageOrder,
        }));
        setMessages(transformedMessages);
      } catch (err) {
        console.error('ConversationRouter load error:', err);
        setError('Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [conversationId, user, setActiveConversationId]);

  const handleTitleUpdate = async (newTitle) => {
    try {
      await apiService.put(`/api/v1/conversations/${conversationId}`, { title: newTitle }, user);
      setConversation(prev => ({ ...prev, title: newTitle }));
      triggerRefresh();
    } catch (err) {
      console.error('ConversationRouter title update error:', err);
    }
  };

  if (loading) {
    return (
      <div className="chat-view">
        <div className="chat-header" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px' }}>
            <div className="skeleton" style={{ width: '60px', height: '24px', borderRadius: '12px' }} />
            <div className="skeleton" style={{ width: '200px', height: '20px', borderRadius: '4px' }} />
          </div>
        </div>
        <div className="message-list" style={{ padding: '24px' }}>
          <SkeletonMessage isUser={false} />
          <SkeletonMessage isUser={true} />
          <SkeletonMessage isUser={false} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-view error">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => navigate('/app/conversations')}>Back to Conversations</button>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="chat-view error">
        <div className="error-message">
          <p>Conversation not found</p>
          <button onClick={() => navigate('/app/conversations')}>Back to Conversations</button>
        </div>
      </div>
    );
  }

  if (conversation.methodology === 'basic') {
    return (
      <AssessmentView
        conversation={conversation}
        messages={messages}
        setMessages={setMessages}
        onTitleUpdate={handleTitleUpdate}
      />
    );
  }

  if (conversation.methodology === 'simple' && conversation.flavor === 'cohort_real_summary' && conversation.cohortRef) {
    return (
      <div className="activity-report-page activity-report-page--full-height">
        <CohortSummaryPage
          cohortId={conversation.cohortRef.cohortId}
          cohortName={conversation.cohortRef.cohortName}
          targetTenantId={conversation.cohortRef.tenantId}
          breadcrumb={
            <div className="arp-breadcrumb">
              <button type="button" className="arp-breadcrumb-link" onClick={() => navigate('/app/conversations')}>
                Conversations
              </button>
              <CaretRight size={11} weight="bold" />
              <span>{conversation.cohortRef.cohortName || 'Cohort Real Summary'}</span>
            </div>
          }
        />
      </div>
    );
  }

  // Cohort Themes discussion — same cohortRef link as the real summary above
  // (both use ref_type 'cohort'); the flavor picks the page. A discussion of a
  // combined report has a cohortSetRef instead. If any of its cohorts has been
  // deleted, it falls through to ChatView, which still works from the saved
  // report, as a single-cohort discussion does when its cohort is deleted.
  const setRef = conversation.cohortSetRef;
  const themesSelection = conversation.cohortRef
    ? { cohortIds: [conversation.cohortRef.cohortId], cohortName: conversation.cohortRef.cohortName, tenantId: conversation.cohortRef.tenantId }
    : setRef && setRef.tenantId != null && setRef.cohorts.length === setRef.cohortIds.length
      ? { cohortIds: setRef.cohortIds, cohortName: cohortSetLabel(setRef.cohorts), tenantId: setRef.tenantId }
      : null;
  if (conversation.methodology === 'simple' && conversation.flavor === 'cohort_themes' && themesSelection) {
    return (
      <div className="activity-report-page activity-report-page--full-height">
        <CohortThemesPage
          cohortIds={themesSelection.cohortIds}
          cohortName={themesSelection.cohortName}
          targetTenantId={themesSelection.tenantId}
          breadcrumb={
            <div className="arp-breadcrumb">
              <button type="button" className="arp-breadcrumb-link" onClick={() => navigate('/app/conversations')}>
                Conversations
              </button>
              <CaretRight size={11} weight="bold" />
              <span>{themesSelection.cohortName || 'Cohort Themes'}</span>
            </div>
          }
        />
      </div>
    );
  }

  if (conversation.methodology === 'real') {
    return (
      <RoleplayView
        conversationId={conversationId}
        conversation={conversation}
        conversationMessages={messages}
        onTitleUpdate={handleTitleUpdate}
      />
    );
  }

  // experiential (or any future methodology) → standard chat
  return (
    <ChatView
      conversationId={conversationId}
      conversation={conversation}
      messages={messages}
      setMessages={setMessages}
      onTitleUpdate={handleTitleUpdate}
    />
  );
};

export default ConversationRouter;